import axios, { type AxiosError } from "axios";
import { classifyError, withRetry } from "@/lib/session-manager";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4205";

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Send cookies for BetterAuth
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 second timeout
});

// BYK-170: Track auth errors separately from network errors
let consecutiveAuthErrors = 0;
const MAX_AUTH_ERRORS_BEFORE_LOGOUT = 2;

// BYK-173: Track network errors for UI feedback
let isNetworkError = false;

/**
 * Get the current network error state
 */
export function getNetworkErrorState(): boolean {
  return isNetworkError;
}

/**
 * Notify listeners of network error state changes
 */
function setNetworkErrorState(hasError: boolean, message?: string): void {
  if (isNetworkError !== hasError) {
    isNetworkError = hasError;
    window.dispatchEvent(
      new CustomEvent("network:status", {
        detail: { offline: hasError, message },
      })
    );
  }
}

/**
 * Show a graceful session expired notification before logout
 * Creates a non-intrusive overlay that informs the user
 */
function showSessionExpiredNotification(): Promise<void> {
  return new Promise((resolve) => {
    // Check if notification already exists
    if (document.getElementById("session-expired-overlay")) {
      resolve();
      return;
    }

    // Create overlay
    const overlay = document.createElement("div");
    overlay.id = "session-expired-overlay";
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      animation: fadeIn 0.2s ease-out;
    `;

    // Create notification card
    const card = document.createElement("div");
    card.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px 32px;
      max-width: 400px;
      text-align: center;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      animation: slideUp 0.3s ease-out;
    `;

    card.innerHTML = `
      <div style="margin-bottom: 16px;">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6366f1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin: 0 auto;">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      </div>
      <h2 style="margin: 0 0 8px; font-size: 18px; font-weight: 600; color: #1f2937;">Session Expired</h2>
      <p style="margin: 0 0 20px; font-size: 14px; color: #6b7280; line-height: 1.5;">
        Your session has expired for security. Redirecting you to sign in again...
      </p>
      <div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: #6366f1; font-size: 14px;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation: spin 1s linear infinite;">
          <path d="M21 12a9 9 0 11-6.219-8.56"></path>
        </svg>
        Redirecting...
      </div>
    `;

    overlay.appendChild(card);

    // Add keyframes animation styles
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(overlay);

    // Resolve after brief delay for user to see the message
    setTimeout(resolve, 1500);
  });
}

// Response interceptor for error handling (BYK-170, 173)
api.interceptors.response.use(
  (response) => {
    // Reset error counters on success
    consecutiveAuthErrors = 0;
    setNetworkErrorState(false);
    return response;
  },
  async (error: AxiosError) => {
    // BYK-173: Classify the error to determine handling
    const classified = classifyError(error);
    console.warn(`[API] Error classified as ${classified.type}:`, classified.message);

    // Handle based on error type
    if (classified.type === "auth") {
      consecutiveAuthErrors++;
      console.warn(`[API] Auth error (count: ${consecutiveAuthErrors}/${MAX_AUTH_ERRORS_BEFORE_LOGOUT})`);

      // Dispatch event for auth store to attempt silent refresh
      window.dispatchEvent(new CustomEvent("auth:session-expired"));

      // Only redirect to login after multiple consecutive auth errors
      if (classified.shouldLogout && consecutiveAuthErrors >= MAX_AUTH_ERRORS_BEFORE_LOGOUT) {
        console.warn("[API] Session expired, showing graceful logout...");
        await showSessionExpiredNotification();
        localStorage.removeItem("byk-auth");
        window.location.href = "/login";
      }
    } else if (classified.type === "network") {
      // BYK-173: Network errors should NOT trigger logout
      console.warn("[API] Network error - NOT logging out:", classified.message);
      setNetworkErrorState(true, classified.message);

      // Reset auth error counter - network issues aren't auth failures
      consecutiveAuthErrors = 0;
    }

    return Promise.reject(error);
  }
);

/**
 * Make a request with automatic retry for network errors (BYK-173)
 */
export async function apiWithRetry<T>(
  requestFn: () => Promise<T>,
  options?: { maxAttempts?: number }
): Promise<T> {
  return withRetry(requestFn, {
    maxAttempts: options?.maxAttempts ?? 3,
    shouldRetry: (error) => {
      const classified = classifyError(error);
      return classified.type === "network" && classified.shouldRetry;
    },
  });
}

export default api;
